import type { HeaderSearchRecentsApi } from "./HeaderSearchRecentsContext";

/**
 * Search result rows on the search page render outside the header provider.
 * Each mounted header-search hook registers here while enableHomeV2 is on.
 * The newest registration wins. Releasing it restores the previous one, so
 * a search-page panel can unmount without silencing the header recorder.
 * Off, or before a hook mounts, writes no-op.
 */
type HeaderSearchRecorder = Pick<
  HeaderSearchRecentsApi,
  | "rememberSearch"
  | "rememberPerson"
  | "rememberOpenedPerson"
  | "rememberOpenedView"
  | "rememberViewed"
>;

type HeaderSearchHit = {
  query?: string | null;
  kind: user_prefs.RecentSearchKind;
  person?: user_prefs.RecentlyViewedPerson | null;
  viewed?: user_prefs.RecentlyViewedItem | null;
};

type Registration = {
  id: number;
  api: HeaderSearchRecorder;
};

let nextId = 1;
let stack: Registration[] = [];
let current: HeaderSearchRecorder | null = null;

const publish = () => {
  current = stack.at(-1)?.api ?? null;
};

export const acquireHeaderSearchRecorder = (api: HeaderSearchRecorder) => {
  const id = nextId;
  nextId += 1;
  stack = [...stack, { id, api }];
  publish();
  return id;
};

export const updateHeaderSearchRecorder = (
  id: number,
  api: HeaderSearchRecorder
) => {
  if (!stack.some((entry) => entry.id === id)) {
    return;
  }
  stack = stack.map((entry) => (entry.id === id ? { id, api } : entry));
  publish();
};

export const releaseHeaderSearchRecorder = (id: number) => {
  stack = stack.filter((entry) => entry.id !== id);
  publish();
};

export const setHeaderSearchRecorder = (next: HeaderSearchRecorder | null) => {
  if (!next) {
    stack = [];
    current = null;
    return;
  }
  current = next;
};

export const readHeaderSearchRecorder = () => current;

export const rememberHeaderSearchHit = (
  fromContext: HeaderSearchRecorder | null | undefined,
  hit: HeaderSearchHit
) => {
  const api = fromContext ?? current;
  if (!api) {
    return;
  }
  const query = hit.query?.trim() ?? "";
  const recentSearch = query ? { query, kind: hit.kind } : null;
  const viewedName = hit.viewed?.name?.trim() ?? "";
  const viewed =
    hit.viewed?.id && viewedName ? { ...hit.viewed, name: viewedName } : null;
  if (viewed && recentSearch) {
    api.rememberOpenedView(viewed, recentSearch);
    return;
  }
  if (viewed) {
    api.rememberViewed(viewed);
    return;
  }
  const name = hit.person?.name?.trim() ?? "";
  const person = hit.person?.id && name ? { ...hit.person, name } : null;
  if (person && recentSearch) {
    api.rememberOpenedPerson(person, recentSearch);
    return;
  }
  if (person) {
    api.rememberPerson(person);
    return;
  }
  if (recentSearch) {
    api.rememberSearch(recentSearch);
  }
};
